import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, CheckCircle2, Save, CalendarClock, ArrowRight, MapPin, Phone } from "lucide-react";
import { http } from "@/lib/api";
import { Card, PageTitle, StatusPill, btnPrimary, btnGhost, fieldCls } from "@/components/portal/ui";
import Deliverable from "@/components/portal/Deliverable";
import { useContact, telHref } from "@/lib/contact";
import { trackEvent } from "@/lib/api";

const Field = ({ label, children, hint }) => (
  <div><label className="block text-sm text-[#6B665E] mb-2">{label}</label>{children}{hint && <p className="text-xs text-[#8A998C] mt-1">{hint}</p>}</div>
);
const PREP = [
  "Confirm the sleep space is ready (crib, room, blackout)", "Have your current routine written down",
  "Note any pediatrician guidance", "Plan where the consultant will observe from overnight",
  "Arrange access/parking details with the consultant", "Prepare any questions for the morning debrief",
];

export default function InHome() {
  const nav = useNavigate();
  const [d, setD] = useState(undefined);
  const [ob, setOb] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const [tab, setTab] = useState("status");
  const timer = useRef();
  const firedRef = useRef(false);
  const { phone } = useContact();

  const load = () => http.get("/portal/inhome").then((r) => { setD(r.data); if (r.data?.onboarding) setOb(r.data.onboarding); if (r.data && !firedRef.current) { firedRef.current = true; trackEvent("in_home_started", {}, { once: true }); } }).catch(() => setD(null));
  useEffect(() => { load(); }, []);

  if (d === undefined) return <div className="text-[#6B665E]">Loading…</div>;

  if (!d) return (
    <div>
      <PageTitle eyebrow="In-Home + Overnight" title="Premium hands-on support" sub="The highest-touch service, in your own home. Not medical monitoring — professional coaching within scope." />
      <Card className="p-8 text-center">
        <Home className="mx-auto text-[#9F634E]" size={40} />
        <p className="font-display text-2xl text-[#1A1918] mt-4">In-Home + Overnight isn't active</p>
        <p className="text-[#6B665E] mt-2 text-sm">Available in select service areas, subject to capacity.</p>
        <button onClick={() => nav("/in-home-infant-sleep-consulting")} className={`${btnPrimary} mt-6`}>Explore In-Home + Overnight — $2,549</button>
      </Card>
    </div>
  );

  if (!d.clarity_completed) return <StageGate body="Every family starts with Clarity. Complete your Clarity intake first so your Certified Infant Sleep Consultant understands the full picture before the visit." cta="Start your Clarity intake" to="/portal/diagnostic" nav={nav} />;
  if (!d.plan_completed) return <StageGate body="Next we complete your Personalized Blueprint, so there's a clear plan for the in-home and overnight support to follow." cta="Complete your Blueprint intake" to="/portal/blueprint-intake" nav={nav} />;

  const state = d.service?.state;
  const onboardingDone = !["Purchased", "Onboarding"].includes(state);

  const set = (k, v) => {
    setOb((prev) => {
      const next = { ...prev, [k]: v };
      clearTimeout(timer.current);
      timer.current = setTimeout(async () => { try { await http.put("/portal/intake/inhome", { answers: next }); setSavedAt(new Date()); } catch (e) {} }, 700);
      return next;
    });
  };

  // Onboarding
  if (!onboardingDone) {
    const submit = async () => {
      try { await http.put("/portal/intake/inhome", { answers: ob }); await http.post("/portal/intake/inhome/submit"); toast.success("Onboarding submitted — we'll confirm scheduling"); load(); }
      catch (e) { toast.error("Could not submit"); }
    };
    return (
      <div>
        <PageTitle eyebrow="In-Home + Overnight onboarding" title="Let's prepare for your visit" sub="We already have your Clarity and Blueprint. These details help your Certified Infant Sleep Consultant confirm the service area and prepare for the overnight." />
        <div className="flex items-center gap-2 text-sm text-[#8A998C] mb-5"><Save size={14} /> {savedAt ? `Saved ${savedAt.toLocaleTimeString()}` : "Your progress saves automatically"}</div>
        <div className="max-w-2xl space-y-4">
          <Card className="p-6 space-y-5">
            <div className="eyebrow text-[#9F634E]">Location & access</div>
            <Field label="Full service address" hint="Used only to confirm you're within the service area."><textarea rows={2} data-testid="inhome-address" className={fieldCls} value={ob.service_address || ""} onChange={(e) => set("service_address", e.target.value)} /></Field>
            <Field label="Parking & entry access"><textarea rows={2} className={fieldCls} value={ob.parking_access || ""} onChange={(e) => set("parking_access", e.target.value)} /></Field>
            <Field label="Best contact phone number"><input data-testid="inhome-contact" className={fieldCls} value={ob.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
          </Card>
          <Card className="p-6 space-y-5">
            <div className="eyebrow text-[#9F634E]">Home & household</div>
            <Field label="Who lives in the home (adults, children)?"><textarea rows={2} className={fieldCls} value={ob.household_members || ""} onChange={(e) => set("household_members", e.target.value)} /></Field>
            <Field label="Any pets we should know about?"><input className={fieldCls} value={ob.pets || ""} onChange={(e) => set("pets", e.target.value)} /></Field>
            <Field label="Where does everyone currently sleep (rooms, arrangement)?"><textarea rows={2} className={fieldCls} value={ob.sleeping_locations || ""} onChange={(e) => set("sleeping_locations", e.target.value)} /></Field>
            <Field label="Nursery / sleep environment details (crib, lighting, sound, temp)"><textarea rows={2} className={fieldCls} value={ob.nursery_details || ""} onChange={(e) => set("nursery_details", e.target.value)} /></Field>
            <Field label="Current bedtime setup & routine"><textarea rows={2} className={fieldCls} value={ob.bedtime_setup || ""} onChange={(e) => set("bedtime_setup", e.target.value)} /></Field>
          </Card>
          <Card className="p-6 space-y-5">
            <div className="eyebrow text-[#9F634E]">Scheduling</div>
            <Field label="Preferred date(s) for the overnight visit"><input data-testid="inhome-dates" className={fieldCls} placeholder="e.g. weekend of the 14th, or any weeknight next week" value={ob.preferred_dates || ""} onChange={(e) => set("preferred_dates", e.target.value)} /></Field>
            <Field label="What are you hoping the overnight helps with most?"><textarea rows={2} className={fieldCls} value={ob.expectations || ""} onChange={(e) => set("expectations", e.target.value)} /></Field>
          </Card>
          <div className="flex gap-3">
            <button onClick={async () => { await http.put("/portal/intake/inhome", { answers: ob }); toast.success("Progress saved"); }} className={btnGhost}>Save & continue later</button>
            <button data-testid="inhome-onboard-submit" onClick={submit} className={btnPrimary}>Submit onboarding <ArrowRight size={15} className="inline ml-1" /></button>
          </div>
        </div>
      </div>
    );
  }

  // Post-onboarding: organized into labeled sub-sections.
  const TABS = [
    ["status", "Service Status", CheckCircle2],
    ["scheduling", "Scheduling", CalendarClock],
    ["prep", "Preparation", MapPin],
    ["visit", "Visit Details", Home],
    ["followup", "Follow-up", ArrowRight],
  ];
  return (
    <div>
      <PageTitle eyebrow="In-Home + Overnight" title="Premium hands-on support" sub="Not medical monitoring — professional coaching within scope, in your own home." />

      <div className="flex flex-wrap gap-1.5 mb-6 border-b border-[#E5E1D8]" data-testid="inhome-tabs">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`itab-${key}`}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm rounded-t-lg -mb-px border-b-2 transition-colors ${tab === key ? "border-[#9F634E] text-[#9F634E] font-medium" : "border-transparent text-[#6B665E] hover:text-[#1A1918]"}`}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {tab === "status" && (
        <Card className="p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3" data-testid="ipanel-status">
          <div>
            <div className="eyebrow text-[#8A998C] mb-1">Service status</div>
            <p className="text-[#1A1918] text-[15px]">{d.service?.next_action || "Your Certified Infant Sleep Consultant is coordinating your visit."}</p>
          </div>
          <StatusPill state={state} />
        </Card>
      )}

      {tab === "scheduling" && (
        <Card className="p-6" data-testid="ipanel-scheduling">
          <div className="eyebrow text-[#9F634E] mb-3 flex items-center gap-1.5"><CalendarClock size={14} /> Scheduling</div>
          {d.scheduled_date ? (
            <div>
              <p className="text-[#1A1918] text-[15px]">Your overnight is confirmed for <span className="font-medium">{d.scheduled_date}</span>{d.arrival_time ? ` · arrival ${d.arrival_time}` : ""}.</p>
              {d.reminder_status === "sent" ? (
                <p className="text-[#4d6b4d] text-xs mt-2">✓ A reminder with your preparation checklist was sent to your email.</p>
              ) : d.reminder_status === "scheduled" ? (
                <p className="text-[#8A998C] text-xs mt-2">We'll email you a reminder about 24 hours before your visit.</p>
              ) : null}
            </div>
          ) : (
            <div>
              {d.onboarding?.preferred_dates && <p className="text-[#1A1918] text-[15px] mb-2">Your preferred dates: <span className="font-medium">{d.onboarding.preferred_dates}</span></p>}
              <p className="text-[#6B665E] text-sm">Your onboarding is in. Your Certified Infant Sleep Consultant will confirm a date based on your preferred availability and service-area capacity.</p>
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-[#F0ECE4] flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="inhome-dayof-phone">
            <div>
              <div className="eyebrow text-[#9F634E] mb-0.5 flex items-center gap-1.5"><Phone size={13} /> Day-of coordination</div>
              <p className="text-[#6B665E] text-sm">For arrival timing or anything urgent on the day of your visit, call or text us directly.</p>
            </div>
            <a href={telHref(phone)} data-testid="inhome-call" className={`${btnPrimary} shrink-0 inline-flex items-center gap-2`}><Phone size={15} /> {phone}</a>
          </div>
        </Card>
      )}

      {tab === "prep" && (
        <Card className="p-6" data-testid="ipanel-prep">
          <div className="eyebrow text-[#9F634E] mb-4 flex items-center gap-1.5"><MapPin size={14} /> Overnight Preparation Checklist</div>
          <ul className="space-y-3">
            {PREP.map((p) => (
              <li key={p} className="flex items-start gap-3 text-[15px] text-[#1A1918]"><CheckCircle2 size={18} className="text-[#8A998C] mt-0.5 shrink-0" /> {p}</li>
            ))}
          </ul>
        </Card>
      )}

      {tab === "visit" && (
        <Card className="p-6" data-testid="ipanel-visit">
          <div className="eyebrow text-[#9F634E] mb-3 flex items-center gap-1.5"><Home size={14} /> Visit Details</div>
          {d.visit_details ? (
            <p className="text-[#1A1918] text-[15px] whitespace-pre-wrap leading-relaxed">{d.visit_details}</p>
          ) : (
            <p className="text-[#6B665E] text-sm">Once your visit is confirmed, your Certified Infant Sleep Consultant's arrival plan and what to expect during the overnight will appear here.</p>
          )}
        </Card>
      )}

      {tab === "followup" && (
        d.followup ? (
          <div data-testid="ipanel-followup">
            <div className="eyebrow text-[#9F634E] mb-3">Your follow-up from the overnight</div>
            <Deliverable report={d.followup} product="inhome_overnight" />
          </div>
        ) : (
          <Card className="p-6" data-testid="ipanel-followup">
            <div className="eyebrow text-[#8A998C] mb-2">After your overnight</div>
            <p className="text-[#6B665E] text-sm">Your Certified Infant Sleep Consultant's overnight observations, morning debrief, and follow-up recommendations will be published here.</p>
          </Card>
        )
      )}
    </div>
  );
}

function StageGate({ body, cta, to, nav }) {
  return (
    <div>
      <PageTitle eyebrow="In-Home + Overnight" title="Premium hands-on support" sub="Your in-home support follows the same professional sequence every family gets." />
      <Card className="p-8 text-center">
        <CheckCircle2 className="mx-auto text-[#9F634E]" size={40} />
        <p className="font-display text-2xl text-[#1A1918] mt-4">First, the stages that come before your visit</p>
        <p className="text-[#6B665E] mt-2 text-sm max-w-md mx-auto">{body}</p>
        <button onClick={() => nav(to)} className={`${btnPrimary} mt-6`}>{cta}</button>
      </Card>
    </div>
  );
}
